import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { computed } from 'vue'

import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { useUserPersonaStore } from './user-persona'

/**
 * 联系人数据结构
 * 联系人是对角色卡的引用，多个联系人可以引用同一个角色卡
 * 支持单聊和群聊两种类型
 */
export interface Contact {
  /** 联系人唯一ID */
  id: string
  /** 联系人名称（可以和角色卡名称不同） */
  name: string
  /** 联系人头像（可选，如果没有则使用角色卡头像） */
  avatar?: string
  /** 引用的角色卡ID（群聊时为空字符串） */
  cardId: string
  /** 所属分组ID */
  groupId?: string | null
  /** 显示模型ID（VRM/Live2D） */
  displayModelId?: string | null
  /** 为该联系人启用的世界书ID列表 */
  worldBookIds?: string[]
  /** 为该联系人启用的表情包组ID列表 */
  stickerPackIds?: string[]
  /** 创建时间 */
  createdAt: number
  /** 更新时间 */
  updatedAt: number
  /** 是否为群聊 */
  isGroup?: boolean
  /** 群聊成员ID列表（群聊专用） */
  members?: string[]
  /** 绑定的用户人格ID（null/undefined 表示使用全局默认人格） */
  boundPersonaId?: number | null
  /** 距离关系：同城(same_city) / 异地(long_distance)，默认异地 */
  locationType?: 'same_city' | 'long_distance'
  /** 是否允许 AI 主动发起线下见面（默认 false） */
  offlineModeEnabled?: boolean
  /** 是否允许该联系人通过后台服务主动发送消息（默认 true） */
  backgroundMessageEnabled?: boolean
  /** 是否向 AI 注入时间感知（当前时间、距离上次聊天、时段建议等，默认 true） */
  timeAwarenessEnabled?: boolean
  /** 是否允许 AI 主动拉黑用户（默认 true，关闭后 AI 调用 block_user 会被忽略） */
  aiBlockUserEnabled?: boolean
  /** 拉黑状态；null/undefined 表示关系正常。保留作旧数据兼容，新数据使用 blockStatusByPersona。 */
  blockStatus?: BlockState | null
  /** 按用户人格隔离的拉黑状态，key 为 persona:<id> 或 persona:default。 */
  blockStatusByPersona?: Record<string, BlockState | null>
  /** 角色拍一拍后缀（用户拍 AI 时显示，如「的小脑袋」） */
  charPatSuffix?: string
  /** 用户拍一拍后缀（AI 反拍用户时显示，如「的脑袋」） */
  userPatSuffix?: string
  /** 是否显示角色思考内容气泡（inner_voice），默认 true */
  innerVoiceEnabled?: boolean
  /** 其他元数据 */
  metadata?: Record<string, any>
}

/**
 * 拉黑状态枚举
 */
export type BlockStatus
  = | 'blocked_by_user' // 用户拉黑了 AI
    | 'blocked_by_ai' // AI 拉黑了用户
    | 'pending_user_approval' // AI 反思后申请加回，等用户接受/拒绝
    | 'pending_ai_approval' // 用户提交申请理由，等 AI 决策
    | 'pending_system_reflection' // 冷静期到期，AI 正在生成申请理由（瞬态）

/**
 * 联系人拉黑状态结构
 */
export interface BlockState {
  status: BlockStatus
  /** 进入当前状态的时间戳，用于冷静期判定 */
  timestamp: number
  /** 仅 pending_user_approval / pending_ai_approval 时存在 */
  applicationReason?: string
  /** 该次拉黑的冷静期（分钟）。仅在 blocked_by_user 触发时记录，由后台扫描读取。 */
  cooldownMinutes?: number
}

/**
 * 联系人创建参数
 */
export interface CreateContactParams {
  /** 联系人名称 */
  name: string
  /** 引用的角色卡ID（群聊时为空字符串） */
  cardId: string
  /** 联系人头像（可选） */
  avatar?: string
  /** 所属分组ID */
  groupId?: string | null
  /** 显示模型ID */
  displayModelId?: string | null
  /** 为该联系人启用的世界书ID列表 */
  worldBookIds?: string[]
  /** 为该联系人启用的表情包组ID列表 */
  stickerPackIds?: string[]
  /** 绑定的用户人格ID */
  boundPersonaId?: number | null
  /** 距离关系 */
  locationType?: 'same_city' | 'long_distance'
  /** 是否允许 AI 主动发起线下见面 */
  offlineModeEnabled?: boolean
  /** 是否允许该联系人通过后台服务主动发送消息（默认 true） */
  backgroundMessageEnabled?: boolean
  /** 是否向 AI 注入时间感知（默认 true） */
  timeAwarenessEnabled?: boolean
  /** 是否允许 AI 主动拉黑用户（默认 true） */
  aiBlockUserEnabled?: boolean
  /** 是否为群聊 */
  isGroup?: boolean
  /** 群聊成员ID列表（群聊专用） */
  members?: string[]
  /** 其他元数据 */
  metadata?: Record<string, any>
}

/**
 * 联系人更新参数
 */
export interface UpdateContactParams {
  /** 联系人名称 */
  name?: string
  /** 联系人头像 */
  avatar?: string
  /** 引用的角色卡ID */
  cardId?: string
  /** 所属分组ID */
  groupId?: string | null
  /** 显示模型ID */
  displayModelId?: string | null
  /** 为该联系人启用的世界书ID列表 */
  worldBookIds?: string[]
  /** 为该联系人启用的表情包组ID列表 */
  stickerPackIds?: string[]
  /** 是否为群聊 */
  isGroup?: boolean
  /** 群聊成员ID列表（群聊专用） */
  members?: string[]
  /** 绑定的用户人格ID */
  boundPersonaId?: number | null
  /** 距离关系 */
  locationType?: 'same_city' | 'long_distance'
  /** 是否允许 AI 主动发起线下见面 */
  offlineModeEnabled?: boolean
  /** 是否允许该联系人通过后台服务主动发送消息（默认 true） */
  backgroundMessageEnabled?: boolean
  /** 是否向 AI 注入时间感知（默认 true） */
  timeAwarenessEnabled?: boolean
  /** 是否允许 AI 主动拉黑用户（默认 true） */
  aiBlockUserEnabled?: boolean
  /** 角色拍一拍后缀 */
  charPatSuffix?: string
  /** 用户拍一拍后缀 */
  userPatSuffix?: string
  /** 是否显示角色思考内容气泡（inner_voice），默认 true */
  innerVoiceEnabled?: boolean
  /** 其他元数据 */
  metadata?: Record<string, any>
}

export const useContactsStore = defineStore('contacts', () => {
  // 使用 IndexedDB 存储代替 localStorage，支持更大的存储容量
  const contactsStorage = useIndexedDBStorage<Record<string, Contact>>('contacts', {}, {
    serializer: {
      read: (v: string) => {
        try {
          const data = JSON.parse(v)
          // 验证数据格式
          const validContacts: Record<string, Contact> = {}
          for (const [id, contact] of Object.entries(data)) {
            if (!contact || typeof contact !== 'object') {
              console.warn(`⚠️ 联系人 ${id} 数据无效，跳过`)
              continue
            }

            const c = contact as any

            // 必须有名称
            if (!c.name) {
              console.warn(`⚠️ 联系人 ${id} 缺少名称，跳过`)
              continue
            }

            // 群聊验证：isGroup 为 true 即可
            // 单聊验证：必须有 cardId（非空字符串）
            const isGroupChat = c.isGroup === true
            const hasSingleChatCardId = typeof c.cardId === 'string' && c.cardId.length > 0

            if (!isGroupChat && !hasSingleChatCardId) {
              console.warn(`⚠️ 联系人 ${id} (${c.name}) 既不是群聊也没有有效的cardId，跳过`)
              continue
            }

            // 验证通过，保留联系人
            validContacts[id] = contact as Contact
          }

          console.debug(`📋 联系人加载完成，共 ${Object.keys(validContacts).length} 个有效联系人`)
          return validContacts
        }
        catch (error) {
          console.error('❌ 解析联系人数据失败:', error)
          return {}
        }
      },
      write: (v: Record<string, Contact>) => JSON.stringify(v),
    },
    onError: (error) => {
      console.error('❌ 联系人存储错误:', error)
    },
  })

  // 使用 IndexedDB 存储当前激活的联系人ID
  const activeContactIdStorage = useIndexedDBStorage<string | null>('active-contact-id', null)

  // 使用 IndexedDB 存储置顶联系人ID列表
  const pinnedContactIdsStorage = useIndexedDBStorage<string[]>('pinned-contact-ids', [])

  // 创建响应式引用
  const contacts = contactsStorage.data
  const activeContactId = activeContactIdStorage.data
  const pinnedContactIds = pinnedContactIdsStorage.data
  const personaStore = useUserPersonaStore()

  const DEFAULT_BLOCK_PERSONA_KEY = 'persona:default'

  function getBlockPersonaKey(contact: Pick<Contact, 'boundPersonaId'> | null | undefined): string {
    const persona = personaStore.resolvePersonaForContact(contact)
    return persona ? `persona:${persona.id}` : DEFAULT_BLOCK_PERSONA_KEY
  }

  function getEffectiveBlockStatus(contact: Contact | null | undefined): BlockState | null {
    if (!contact)
      return null
    const key = getBlockPersonaKey(contact)
    // 优先读 blockStatusByPersona（新格式）
    if (contact.blockStatusByPersona && Object.prototype.hasOwnProperty.call(contact.blockStatusByPersona, key))
      return contact.blockStatusByPersona[key] ?? null
    // 兼容旧格式：只有当 blockStatus 不是 null 时才返回（null 表示已迁移但该人格下无拉黑）
    if (contact.blockStatus !== undefined && contact.blockStatus !== null)
      return contact.blockStatus
    return null
  }

  /**
   * 按 id 拿 effective blockStatus（不复制整个 contact 对象，给列表渲染用）
   * 与 getContact 相比：避免每个联系人都做 spread，N 个联系人时是 O(N) vs O(N) 浅拷贝
   */
  function getEffectiveBlockStatusById(contactId: string): BlockState | null {
    return getEffectiveBlockStatus(contacts.value[contactId])
  }

  function withEffectiveBlockStatus(contact: Contact): Contact {
    return {
      ...contact,
      blockStatus: getEffectiveBlockStatus(contact),
    }
  }

  // 存储加载状态
  const isLoaded = computed(() => contactsStorage.isLoaded.value && activeContactIdStorage.isLoaded.value && pinnedContactIdsStorage.isLoaded.value)
  const isLoading = computed(() => contactsStorage.isLoading.value || activeContactIdStorage.isLoading.value || pinnedContactIdsStorage.isLoading.value)
  // 只有读取失败才提供重新加载；写入失败由存储工具提示重试保存，避免丢弃内存更改。
  const storageError = computed(() => contactsStorage.loadError.value || activeContactIdStorage.loadError.value || pinnedContactIdsStorage.loadError.value)

  // 当前激活的联系人
  const activeContact = computed(() => {
    if (!activeContactId.value)
      return null
    const contact = contacts.value[activeContactId.value]
    return contact ? withEffectiveBlockStatus(contact) : null
  })

  // 联系人列表（数组形式）
  const contactsList = computed(() => {
    // ⚡ 优化：contact.id 本就等于 key，不需要 spread + 覆盖
    return Object.values(contacts.value).map(contact => withEffectiveBlockStatus(contact))
  })

  // 联系人数量
  const contactsCount = computed(() => Object.keys(contacts.value).length)

  /**
   * 等待存储加载完成
   */
  async function waitForLoad(): Promise<void> {
    await Promise.all([
      waitForStorage(contactsStorage),
      waitForStorage(activeContactIdStorage),
      waitForStorage(pinnedContactIdsStorage),
    ])
  }

  async function retryLoad(): Promise<void> {
    const reloads: Promise<void>[] = []
    if (contactsStorage.loadError.value)
      reloads.push(contactsStorage.reload())
    if (activeContactIdStorage.loadError.value)
      reloads.push(activeContactIdStorage.reload())
    if (pinnedContactIdsStorage.loadError.value)
      reloads.push(pinnedContactIdsStorage.reload())
    await Promise.all(reloads)
  }

  /**
   * 获取单个联系人
   */
  function getContact(contactId: string): Contact | null {
    const contact = contacts.value[contactId]
    return contact ? withEffectiveBlockStatus(contact) : null
  }

  /**
   * 创建联系人
   */
  function createContact(params: CreateContactParams): string {
    const contactId = nanoid()
    const now = Date.now()

    const newContact: Contact = {
      id: contactId,
      name: params.name,
      avatar: params.avatar,
      cardId: params.cardId,
      groupId: params.groupId,
      displayModelId: params.displayModelId,
      worldBookIds: params.worldBookIds ?? [],
      stickerPackIds: params.stickerPackIds ?? [],
      boundPersonaId: params.boundPersonaId ?? null,
      locationType: params.locationType || 'long_distance',
      offlineModeEnabled: params.offlineModeEnabled || false,
      backgroundMessageEnabled: params.backgroundMessageEnabled ?? true,
      timeAwarenessEnabled: params.timeAwarenessEnabled ?? true,
      aiBlockUserEnabled: params.aiBlockUserEnabled ?? true,
      isGroup: params.isGroup || false,
      members: params.members || [],
      createdAt: now,
      updatedAt: now,
      metadata: params.metadata || {},
    }

    contacts.value = {
      ...contacts.value,
      [contactId]: newContact,
    }

    return contactId
  }

  /**
   * 更新联系人
   */
  function updateContact(contactId: string, updates: UpdateContactParams): boolean {
    const existingContact = contacts.value[contactId]
    if (!existingContact)
      return false

    const updatedContact: Contact = {
      ...existingContact,
      ...updates,
      updatedAt: Date.now(),
      // 特殊处理 metadata，确保不覆盖未更新的字段
      metadata: updates.metadata
        ? { ...existingContact.metadata, ...updates.metadata }
        : existingContact.metadata,
    }

    contacts.value = {
      ...contacts.value,
      [contactId]: updatedContact,
    }

    // 成员头像变更时，自动刷新包含该成员的群聊头像
    if (updates.avatar !== undefined && !existingContact.isGroup) {
      refreshGroupAvatarsForMember(contactId)
    }

    return true
  }

  /**
   * 成员头像变更时，自动刷新包含该成员的所有群聊头像
   */
  async function refreshGroupAvatarsForMember(memberId: string) {
    try {
      const { buildGroupAvatar } = await import('../utils/group-avatar')
      const groupContacts = Object.values(contacts.value).filter(
        c => c.isGroup && c.members?.includes(memberId),
      )
      if (groupContacts.length === 0)
        return

      const updates: Record<string, Contact> = {}
      await Promise.all(groupContacts.map(async (group) => {
        const avatarUrls = (group.members || []).map(
          id => contacts.value[id]?.avatar || '/BOT.jpg',
        )
        const newAvatar = await buildGroupAvatar(avatarUrls)
        updates[group.id] = { ...contacts.value[group.id], avatar: newAvatar, updatedAt: Date.now() }
      }))

      contacts.value = { ...contacts.value, ...updates }
    }
    catch (e) {
      console.warn('群头像自动刷新失败:', e)
    }
  }

  /**
   * 删除联系人
   */
  function deleteContact(contactId: string): boolean {
    if (!contacts.value[contactId])
      return false

    // 如果删除的是当前激活的联系人，需要切换到其他联系人
    if (activeContactId.value === contactId) {
      const otherContacts = Object.keys(contacts.value).filter(id => id !== contactId)
      activeContactId.value = otherContacts.length > 0 ? otherContacts[0] : null
    }

    // 同时移除置顶状态
    unpinContact(contactId)

    // 清理该联系人的线下模式状态
    try {
      const mapRaw = localStorage.getItem('meowchat-offline-mode-map')
      if (mapRaw) {
        const map = JSON.parse(mapRaw)
        if (contactId in map) {
          delete map[contactId]
          localStorage.setItem('meowchat-offline-mode-map', JSON.stringify(map))
        }
      }
    }
    catch { /* 清理失败不影响删除流程 */ }

    // M4: 联动清理跨 store 引用（聊天/表情/背景/朋友圈/通话/关系/记忆），避免孤儿数据
    // 使用动态 import 避免循环依赖
    Promise.allSettled([
      import('./chat').then(({ useChatStore }) => {
        const store = useChatStore() as any
        if (typeof store.deleteContactChatData === 'function')
          store.deleteContactChatData(contactId)
      }),
      import('./stickers').then(({ useStickersStore }) => {
        const store = useStickersStore() as any
        if (typeof store.deleteContactStickers === 'function')
          store.deleteContactStickers(contactId)
      }),
      import('./contact-backgrounds').then(({ useContactBackgroundsStore }) => {
        const store = useContactBackgroundsStore() as any
        if (typeof store.deleteContactBackground === 'function')
          return store.deleteContactBackground(contactId)
      }),
      import('./posts').then(({ usePostsStore }) => {
        const store = usePostsStore() as any
        if (typeof store.purgeByContactId === 'function')
          store.purgeByContactId(contactId)
      }),
      import('./call').then(({ useCallStore }) => {
        const store = useCallStore() as any
        if (typeof store.purgeByContactId === 'function')
          store.purgeByContactId(contactId)
      }),
      import('./relationship').then(({ useRelationshipStore }) => {
        const store = useRelationshipStore() as any
        if (typeof store.purgeByContactId === 'function')
          store.purgeByContactId(contactId)
      }),
      import('./memory').then(({ useMemoryStore }) => {
        const store = useMemoryStore() as any
        if (typeof store.purgeByContactId === 'function')
          store.purgeByContactId(contactId)
      }),
    ]).then((results) => {
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0)
        console.warn(`⚠️ 删除联系人联动清理：${failed} 个 store 清理失败`)
      else
        console.info(`✅ 联系人 ${contactId} 跨 store 引用已联动清理`)
    })

    // 删除联系人
    const { [contactId]: _removed, ...rest } = contacts.value
    contacts.value = rest

    return true
  }

  /**
   * 根据角色卡ID获取所有使用该角色卡的联系人
   */
  function getContactsByCardId(cardId: string): Contact[] {
    return contactsList.value.filter(contact => contact.cardId === cardId)
  }

  /**
   * 获取所有联系人（用于朋友圈等功能）
   */
  async function getAllContacts(): Promise<Contact[]> {
    // 排除群聊，只返回单聊联系人
    return contactsList.value.filter(contact => !contact.isGroup)
  }

  /**
   * 设置联系人分组
   */
  function setContactGroup(contactId: string, groupId: string | null): boolean {
    return updateContact(contactId, { groupId })
  }

  /**
   * 激活联系人
   */
  function activateContact(contactId: string): boolean {
    if (!contacts.value[contactId])
      return false

    activeContactId.value = contactId

    // 🆕 切换联系人时即时扫描一次拉黑冷静期，避免用户等 60 秒心跳
    // 用动态 import 避免循环依赖
    import('./backgroundActivity').then(({ useBackgroundActivityStore }) => {
      useBackgroundActivityStore().scanBlockCooldowns().catch((e: unknown) => {
        console.warn('⚠️ 切换联系人时拉黑冷静期扫描失败:', e)
      })
    }).catch(() => { /* store 还没初始化时忽略 */ })

    return true
  }

  /**
   * 批量导出联系人
   * 注：blockStatus 是私密的本地状态，不参与导出（避免分享角色卡时把私密拉黑状态带走）
   */
  function exportContacts(contactIds?: string[]): string {
    const sanitize = (contact: Contact): Contact => {
      const { blockStatus: _blockStatus, ...rest } = contact
      return rest as Contact
    }
    const contactsToExport = (contactIds
      ? contactIds.map(id => contacts.value[id]).filter(Boolean)
      : Object.values(contacts.value)
    ).map(sanitize)

    return JSON.stringify({
      version: '1.0',
      exportTime: Date.now(),
      contacts: contactsToExport,
    }, null, 2)
  }

  /**
   * 批量导入联系人
   * 支持单聊和群聊两种类型
   *
   * @param jsonData 序列化后的联系人 JSON（见 exportContacts 格式）
   * @param options 可选配置
   * @param options.personaLookup 可选依赖注入：判断某个 personaId 在当前设备是否存在。
   *   由调用方从 user-persona store 拿 getPersona 传进来即可，避免循环 import。
   */
  function importContacts(
    jsonData: string,
    options?: { personaLookup?: (id: unknown) => boolean },
  ): { success: number, failed: number, errors: string[] } {
    const errors: string[] = []
    let success = 0
    let failed = 0
    const personaLookup = options?.personaLookup

    try {
      const data = JSON.parse(jsonData)

      if (!data.contacts || !Array.isArray(data.contacts)) {
        throw new Error('无效的导入数据格式')
      }

      for (const contactData of data.contacts) {
        try {
          // 验证必需字段：名称是必须的
          if (!contactData.name) {
            throw new Error('缺少必需字段: name')
          }

          // 群聊验证：isGroup 为 true 时不需要 cardId
          // 单聊验证：必须有 cardId
          const isGroupChat = contactData.isGroup === true
          if (!isGroupChat && !contactData.cardId) {
            throw new Error('单聊联系人缺少必需字段: cardId')
          }

          // boundPersonaId：源设备上的 persona id 在本设备不一定存在。
          // 如果调用方提供 personaLookup 且本机能命中同 id 的 persona（同设备/同账号导入），保留绑定；
          // 否则置 null，让联系人走本设备的默认人格。
          // contact schema 里 boundPersonaId 是 number | null，源头不会是字符串，无需 Number() 转换。
          const importedBoundId = contactData.boundPersonaId
          const safeBoundPersonaId = typeof importedBoundId === 'number' && personaLookup?.(importedBoundId)
            ? importedBoundId
            : null

          createContact({
            name: contactData.name,
            cardId: contactData.cardId || '', // 群聊时 cardId 为空字符串
            avatar: contactData.avatar,
            groupId: contactData.groupId,
            displayModelId: contactData.displayModelId,
            worldBookIds: contactData.worldBookIds,
            stickerPackIds: contactData.stickerPackIds,
            boundPersonaId: safeBoundPersonaId,
            locationType: contactData.locationType,
            offlineModeEnabled: contactData.offlineModeEnabled,
            backgroundMessageEnabled: typeof contactData.backgroundMessageEnabled === 'boolean'
              ? contactData.backgroundMessageEnabled
              : undefined,
            timeAwarenessEnabled: typeof contactData.timeAwarenessEnabled === 'boolean'
              ? contactData.timeAwarenessEnabled
              : undefined,
            isGroup: contactData.isGroup,
            members: contactData.members,
            metadata: contactData.metadata,
          })
          success++
        }
        catch (error) {
          failed++
          errors.push(`导入联系人 "${contactData.name || '未知'}" 失败: ${error}`)
        }
      }
    }
    catch (error) {
      errors.push(`解析数据失败: ${error}`)
      return { success: 0, failed: 0, errors }
    }

    return { success, failed, errors }
  }

  /**
   * 判断联系人是否置顶
   */
  function isPinned(contactId: string): boolean {
    return pinnedContactIds.value.includes(contactId)
  }

  /**
   * 切换联系人置顶状态
   */
  function togglePin(contactId: string): boolean {
    if (!contacts.value[contactId])
      return false

    if (isPinned(contactId)) {
      unpinContact(contactId)
    }
    else {
      pinnedContactIds.value = [...pinnedContactIds.value, contactId]
    }
    return true
  }

  /**
   * 取消置顶联系人
   */
  function unpinContact(contactId: string): void {
    pinnedContactIds.value = pinnedContactIds.value.filter(id => id !== contactId)
  }

  /**
   * 清空所有联系人
   */
  function clearAllContacts(): void {
    contacts.value = {}
    activeContactId.value = null
    pinnedContactIds.value = []
  }

  /**
   * 数据迁移：将旧的角色卡系统迁移到新的联系人系统
   * 如果联系人数据为空，但有角色卡数据，则自动为每个角色卡创建一个联系人
   */
  function migrateFromCards(cardsData: Record<string, any>, currentActiveCardId?: string): { migrated: number, skipped: number } {
    // 如果已经有联系人数据，跳过迁移
    if (Object.keys(contacts.value).length > 0) {
      console.info('⏭️ 已有联系人数据，跳过迁移')
      return { migrated: 0, skipped: Object.keys(cardsData).length }
    }

    // 如果没有角色卡数据，跳过迁移
    if (Object.keys(cardsData).length === 0) {
      console.info('⏭️ 没有角色卡数据，跳过迁移')
      return { migrated: 0, skipped: 0 }
    }

    console.info('🔄 开始迁移角色卡到联系人系统...')
    let migrated = 0
    let skipped = 0
    let newActiveContactId: string | null = null

    for (const [cardId, card] of Object.entries(cardsData)) {
      try {
        // 创建联系人，使用角色卡ID作为基础
        const contactId = createContact({
          name: card.name,
          cardId,
          // 不再自动设置角色卡头像，让用户自己上传
          groupId: card.metadata?.groupId,
          displayModelId: card.metadata?.displayModelId,
        })

        migrated++

        // 如果这是当前激活的角色卡，记录其对应的联系人ID
        if (currentActiveCardId === cardId) {
          newActiveContactId = contactId
        }

        console.info(`✅ 已迁移角色卡: ${card.name} -> 联系人ID: ${contactId}`)
      }
      catch (error) {
        console.error(`❌ 迁移角色卡失败: ${card.name}`, error)
        skipped++
      }
    }

    // 如果找到了对应的联系人，激活它
    if (newActiveContactId) {
      activeContactId.value = newActiveContactId
      console.info(`✅ 已激活迁移后的联系人`)
    }
    // 否则激活第一个联系人
    else if (Object.keys(contacts.value).length > 0) {
      activeContactId.value = Object.keys(contacts.value)[0]
      console.info(`✅ 已激活第一个联系人`)
    }

    console.info(`✅ 迁移完成！成功: ${migrated}, 跳过: ${skipped}`)
    return { migrated, skipped }
  }

  // ============ 拉黑系统 ============

  /**
   * 通知 push-server 停止主动推送（拉黑联动）
   * 仅在进入「完全拉黑」时调用 deleteContactConfig，让后端立刻停止调度。
   *
   * 解除拉黑（blocked_* → null / pending_*）的方向不在这里处理：
   * apps/stage-web/src/composables/init-background-systems.ts 的 contactWorldBookBindings
   * watcher 监听 blockStatus 字段变化，会触发 debounced syncAllContactConfigs
   * 把该联系人重新注册回 push-server。
   *
   * 完全 fire-and-forget，任何失败都不影响本地状态。
   */
  function notifyPushServerStopForContact(contactId: string) {
    import('./offlineSync')
      .then(({ useOfflineSyncStore }) => useOfflineSyncStore().deleteContactConfig(contactId))
      .catch(() => { /* 模块不可用或调用失败：忽略 */ })
  }

  /**
   * 写入拉黑状态（内部辅助）
   */
  function setBlockState(contactId: string, state: BlockState | null): boolean {
    const contact = contacts.value[contactId]
    if (!contact)
      return false
    if (contact.isGroup)
      return false

    const personaKey = getBlockPersonaKey(contact)
    const prevStatus = getEffectiveBlockStatus(contact)?.status ?? null
    const blockStatusByPersona = { ...(contact.blockStatusByPersona || {}) }

    if (state)
      blockStatusByPersona[personaKey] = state
    else
      delete blockStatusByPersona[personaKey]

    const cleanedBlockStatusByPersona = Object.fromEntries(
      Object.entries(blockStatusByPersona).filter(([, value]) => value != null),
    ) as Record<string, BlockState>

    contacts.value = {
      ...contacts.value,
      [contactId]: {
        ...contact,
        // 顶层字段只作为旧数据兼容读取；写入后统一迁移到按人格隔离的 map。
        blockStatus: null,
        blockStatusByPersona: Object.keys(cleanedBlockStatusByPersona).length > 0
          ? cleanedBlockStatusByPersona
          : undefined,
        updatedAt: Date.now(),
      },
    }

    // 拉黑联动 push-server：进入完全拉黑态时让后端立刻停止调度
    const wasFullyBlocked = prevStatus === 'blocked_by_user' || prevStatus === 'blocked_by_ai'
    const nowFullyBlocked = state?.status === 'blocked_by_user' || state?.status === 'blocked_by_ai'
    if (!wasFullyBlocked && nowFullyBlocked)
      notifyPushServerStopForContact(contactId)

    return true
  }

  /**
   * 用户主动拉黑某个联系人
   * @param contactId 联系人 ID
   * @param cooldownMinutes 该次拉黑的冷静期（分钟），缺省时由调用方决定（通常等于全局默认值）
   */
  function blockContactByUser(contactId: string, cooldownMinutes?: number): boolean {
    return setBlockState(contactId, {
      status: 'blocked_by_user',
      timestamp: Date.now(),
      cooldownMinutes,
    })
  }

  /**
   * 用户解除拉黑（仅 blocked_by_user 状态可用）
   */
  function unblockContact(contactId: string): boolean {
    const contact = contacts.value[contactId]
    const blockStatus = getEffectiveBlockStatus(contact)
    if (!blockStatus)
      return false
    if (blockStatus.status !== 'blocked_by_user')
      return false
    return setBlockState(contactId, null)
  }

  /**
   * 用户兜底逃生通道：从 blocked_by_ai 或任一 pending_* 状态强制回到正常关系
   * 适用场景：被 AI 拉黑时模型不调工具卡住、好友申请流程中断、瞬态反思状态卡死等。
   * 不允许从 blocked_by_user 走这里——那是用户主动拉黑，应当走 unblockContact。
   */
  function forceUnblockByUser(contactId: string): boolean {
    const contact = contacts.value[contactId]
    const blockStatus = getEffectiveBlockStatus(contact)
    if (!blockStatus)
      return false
    if (blockStatus.status === 'blocked_by_user')
      return false
    return setBlockState(contactId, null)
  }

  /**
   * AI 工具调用：将用户拉黑（仅单聊生效）
   */
  function setBlockedByAi(contactId: string): boolean {
    return setBlockState(contactId, { status: 'blocked_by_ai', timestamp: Date.now() })
  }

  /**
   * 用户提交好友申请理由，等待 AI 决策
   */
  function requestRefriendFromUser(contactId: string, reason: string): boolean {
    return setBlockState(contactId, {
      status: 'pending_ai_approval',
      timestamp: Date.now(),
      applicationReason: reason,
    })
  }

  /**
   * 用户对 AI 申请的回应
   * - accept → 解除拉黑
   * - reject → 退回 blocked_by_user 状态并重置 timestamp
   *   保留原有 cooldownMinutes（同一次拉黑事件内一致）
   */
  function respondToFriendRequest(contactId: string, decision: 'accept' | 'reject'): boolean {
    const contact = contacts.value[contactId]
    const blockStatus = getEffectiveBlockStatus(contact)
    if (blockStatus?.status !== 'pending_user_approval')
      return false
    if (decision === 'accept')
      return setBlockState(contactId, null)
    const prevCooldown = blockStatus.cooldownMinutes
    return setBlockState(contactId, {
      status: 'blocked_by_user',
      timestamp: Date.now(),
      cooldownMinutes: prevCooldown,
    })
  }

  /**
   * AI 同意用户的好友申请
   */
  function acceptUserRefriendRequest(contactId: string): boolean {
    const contact = contacts.value[contactId]
    if (getEffectiveBlockStatus(contact)?.status !== 'pending_ai_approval')
      return false
    return setBlockState(contactId, null)
  }

  /**
   * AI 拒绝用户的好友申请
   */
  function rejectUserRefriendRequest(contactId: string): boolean {
    const contact = contacts.value[contactId]
    if (getEffectiveBlockStatus(contact)?.status !== 'pending_ai_approval')
      return false
    return setBlockState(contactId, { status: 'blocked_by_ai', timestamp: Date.now() })
  }

  /**
   * 冷静期到期，进入系统反思（瞬态）
   * 保留 cooldownMinutes 以便异常 fallback 时退回原值
   */
  function setPendingSystemReflection(contactId: string): boolean {
    const contact = contacts.value[contactId]
    const blockStatus = getEffectiveBlockStatus(contact)
    if (blockStatus?.status !== 'blocked_by_user')
      return false
    const prevCooldown = blockStatus.cooldownMinutes
    return setBlockState(contactId, {
      status: 'pending_system_reflection',
      timestamp: Date.now(),
      cooldownMinutes: prevCooldown,
    })
  }

  /**
   * AI 生成申请理由后写入，等待用户决策
   */
  function setPendingUserApproval(contactId: string, reason: string): boolean {
    return setBlockState(contactId, {
      status: 'pending_user_approval',
      timestamp: Date.now(),
      applicationReason: reason,
    })
  }

  /**
   * AI 决定不申请 / 异常 fallback：退回 blocked_by_user 并重置 timestamp
   * 保留原 cooldownMinutes
   */
  function resetBlockCooldown(contactId: string): boolean {
    const contact = contacts.value[contactId]
    const prevCooldown = getEffectiveBlockStatus(contact)?.cooldownMinutes
    return setBlockState(contactId, {
      status: 'blocked_by_user',
      timestamp: Date.now(),
      cooldownMinutes: prevCooldown,
    })
  }

  /**
   * 是否被「完全拉黑」（双向 blocked_*），用于过滤后台活动等场景。
   * pending_* 状态需要让 AI 推进流程，不算完全拉黑。
   */
  function isFullyBlocked(contactId: string): boolean {
    const status = getEffectiveBlockStatus(contacts.value[contactId])?.status
    return status === 'blocked_by_user' || status === 'blocked_by_ai'
  }

  /**
   * 是否有任意拉黑相关状态（含 pending）
   */
  function hasBlockStatus(contactId: string): boolean {
    return !!getEffectiveBlockStatus(contacts.value[contactId])
  }

  return {
    // 状态
    contacts,
    activeContactId,
    activeContact,
    contactsList,
    contactsCount,
    pinnedContactIds,

    // 存储状态
    isLoaded,
    isLoading,
    storageError,
    waitForLoad,
    retryLoad,

    // 方法
    createContact,
    updateContact,
    deleteContact,
    getContact,
    getContactsByCardId,
    getAllContacts,
    setContactGroup,
    activateContact,
    exportContacts,
    importContacts,
    clearAllContacts,
    migrateFromCards,
    isPinned,
    togglePin,
    unpinContact,

    // 拉黑系统
    blockContactByUser,
    unblockContact,
    forceUnblockByUser,
    setBlockedByAi,
    requestRefriendFromUser,
    respondToFriendRequest,
    acceptUserRefriendRequest,
    rejectUserRefriendRequest,
    setPendingSystemReflection,
    setPendingUserApproval,
    resetBlockCooldown,
    isFullyBlocked,
    hasBlockStatus,
    getEffectiveBlockStatusById,
  }
})
